import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  applyLanguage,
  resolveLanguage,
  hasExplicitLanguageChoice,
  markExplicitLanguageChoice,
  setPendingLanguageChoice,
} from '../../i18n';
import { isSupportedLanguage } from '../../i18n/supportedLanguages';
import { authAPI } from '../../services/api';

// Thunk: save user preferred language to backend profile and apply it
export const setUserLanguage = createAsyncThunk(
  'language/setUserLanguage',
  async (lang, { getState }) => {
    const state = getState();
    const tenantDefault = state.language.tenantDefaultLanguage;
    const resolved = resolveLanguage(lang, tenantDefault);
    applyLanguage(resolved);
    markExplicitLanguageChoice();
    // The language is applied locally either way, but the caller is told
    // whether it actually reached the profile — a failed write means the
    // choice will not survive on other devices, and claiming otherwise is
    // how "preference saved" ends up being a lie after a refresh.
    let saved = true;
    try {
      await authAPI.updateProfile({ preferredLanguage: lang || null });
    } catch (err) {
      saved = false;
    }
    return { preferredLanguage: lang, resolvedLanguage: resolved, saved };
  }
);

const initialDetected = localStorage.getItem('i18nextLng');

const languageSlice = createSlice({
  name: 'language',
  initialState: {
    currentLanguage: isSupportedLanguage(initialDetected) ? initialDetected : 'en',
    userPreferredLanguage: null,
    tenantDefaultLanguage: null,
    loading: false,
    error: null,
  },
  reducers: {
    // Called after authenticated user/tenant data is loaded — and again on
    // every refresh, so it must never overwrite a language the user picked.
    initLanguage: (state, action) => {
      const { userPreferredLanguage, tenantDefaultLanguage } = action.payload;
      state.userPreferredLanguage = userPreferredLanguage || null;
      state.tenantDefaultLanguage = tenantDefaultLanguage || null;

      // The saved profile preference wins. Failing that, a choice made on this
      // device still outranks the tenant default: the tenant default is only a
      // starting point for users who have never picked a language. Without
      // this, a refresh would drag an English-speaking user on a Turkish-default
      // tenant back to Turkish on every load.
      const resolved = isSupportedLanguage(userPreferredLanguage)
        ? userPreferredLanguage
        : hasExplicitLanguageChoice()
          ? state.currentLanguage
          : resolveLanguage(null, tenantDefaultLanguage, state.currentLanguage);

      state.currentLanguage = resolved;
      applyLanguage(resolved);
    },
    // Called from the public Login page once branding is fetched.
    // Honours explicit user choice (made via the language selector this session)
    // and only otherwise falls back to tenant default → browser detection.
    initLoginLanguage: (state, action) => {
      const tenantDefaultLanguage = action.payload?.tenantDefaultLanguage || null;
      state.tenantDefaultLanguage = tenantDefaultLanguage;
      if (hasExplicitLanguageChoice()) {
        // Respect the user's manual selection
        applyLanguage(state.currentLanguage);
        return;
      }
      const resolved = resolveLanguage(null, tenantDefaultLanguage, state.currentLanguage);
      state.currentLanguage = resolved;
      applyLanguage(resolved);
    },
    setTenantLanguage: (state, action) => {
      const lang = action.payload;
      state.tenantDefaultLanguage = lang;
      // Changing the tenant default re-languages only those who never chose
      // for themselves — neither a saved preference nor a choice on this device.
      if (!state.userPreferredLanguage && !hasExplicitLanguageChoice()) {
        const resolved = resolveLanguage(null, lang, state.currentLanguage);
        state.currentLanguage = resolved;
        applyLanguage(resolved);
      }
    },
    // Quick local switch (e.g. from login screen selector). Marks the choice
    // as explicit so subsequent loads won't override it with tenant default,
    // and parks it so the next authenticated load writes it to the profile.
    switchLanguage: (state, action) => {
      const lang = action.payload;
      state.currentLanguage = lang;
      applyLanguage(lang);
      markExplicitLanguageChoice();
      setPendingLanguageChoice(lang);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(setUserLanguage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(setUserLanguage.fulfilled, (state, action) => {
        state.loading = false;
        state.userPreferredLanguage = action.payload.preferredLanguage;
        state.currentLanguage = action.payload.resolvedLanguage;
      })
      .addCase(setUserLanguage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  initLanguage,
  initLoginLanguage,
  switchLanguage,
  setTenantLanguage,
} = languageSlice.actions;
export default languageSlice.reducer;
